/**
 * The live stream (Server-Sent Events, GET /api/v1/events, specs/c2-kontratlar.md 3). One
 * stream patches the panel state per entity. The server sends a heartbeat every 15 s while
 * its database listener is healthy; the panel treats more than 30 s without one as stale
 * and disables every approve control until the stream is back.
 *
 * Event shapes and the parser are the contract's zod schemas: an unknown or malformed event
 * is dropped instead of patching the state with the wrong shape.
 */

import {
  EVENT_ID_PATTERN,
  HEARTBEAT_INTERVAL_MS,
  type LiveEvent,
  type LiveEventType,
  parseLiveEvent,
  SSE_RECONNECT_BACKOFF_MS,
  STALE_AFTER_MS,
} from '@twin/contracts';

export type { LiveEvent, LiveEventType };
export { HEARTBEAT_INTERVAL_MS, parseLiveEvent, STALE_AFTER_MS };

export type LiveStatus = 'open' | 'error';

export interface LiveEvents {
  /** Starts the stream. Returns a function that closes it. */
  connect(onEvent: (event: LiveEvent) => void, onStatus: (status: LiveStatus) => void): () => void;
  /**
   * The id of the last event the panel processed ("<bootId>.<seq>"), sent as X-Twin-View on
   * approve and hold so the server can refuse a decision made on a stale view (K15).
   */
  lastEventId(): string | null;
}

/** The subset of EventSource the client uses, so tests can pass a fake. */
export interface EventSourceLike {
  readonly readyState: number;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onopen: ((event: Event) => void) | null;
  close(): void;
}

/** EventSource.CLOSED: the browser gave up (an HTTP error) and will not reconnect by itself. */
const CLOSED = 2;

/**
 * No message at all (not even a heartbeat) for this long means the connection is dead or a
 * proxy is buffering it: the client closes it and connects again.
 */
export const SILENCE_RECONNECT_MS = 2 * STALE_AFTER_MS;

export interface EventSourceLiveOptions {
  factory?: (url: string) => EventSourceLike;
  backoffMs?: readonly number[];
  /** Local clock for the silence watchdog (Date.now by default). */
  now?: () => number;
}

/**
 * Live events over a real EventSource (same origin, so the Tailscale session and the edge
 * header travel with it). EventSource reconnects by itself after a dropped connection and
 * sends Last-Event-ID; after an HTTP error it closes for good (readyState CLOSED), so the
 * client builds a new one with backoff (1, 2, 4, 8, 16, 30 s). A stream that stays silent for
 * 60 s is rebuilt the same way. Every lost connection is reported as 'error' (the panel goes
 * stale); every open as 'open' (the panel reloads the snapshot).
 */
export function createEventSourceLive(url: string, options: EventSourceLiveOptions = {}): LiveEvents {
  const factory = options.factory ?? ((target: string) => new EventSource(target, { withCredentials: true }));
  const backoff = options.backoffMs ?? SSE_RECONNECT_BACKOFF_MS;
  const now = options.now ?? (() => Date.now());
  let lastId: string | null = null;

  return {
    lastEventId: () => lastId,
    connect(onEvent, onStatus) {
      let source: EventSourceLike | null = null;
      let attempt = 0;
      let retry: ReturnType<typeof setTimeout> | null = null;
      let watchdog: ReturnType<typeof setInterval> | null = null;
      let lastMessageAt = now();
      let closed = false;

      const scheduleReconnect = () => {
        if (closed || retry !== null) return;
        const delay = backoff[Math.min(attempt, backoff.length - 1)] ?? 30_000;
        attempt += 1;
        retry = setTimeout(() => {
          retry = null;
          open();
        }, delay);
      };

      const drop = () => {
        source?.close();
        source = null;
        onStatus('error');
        scheduleReconnect();
      };

      const open = () => {
        if (closed) return;
        const current = factory(url);
        source = current;
        lastMessageAt = now();
        current.onopen = () => {
          if (source !== current) return;
          attempt = 0;
          lastMessageAt = now();
          onStatus('open');
        };
        current.onerror = () => {
          if (source !== current) return;
          if (current.readyState === CLOSED) drop();
          // CONNECTING: the browser retries by itself with Last-Event-ID; until then the data is stale.
          else onStatus('error');
        };
        current.onmessage = (message) => {
          if (source !== current) return;
          lastMessageAt = now();
          if (typeof message.lastEventId === 'string' && EVENT_ID_PATTERN.test(message.lastEventId)) {
            lastId = message.lastEventId;
          }
          const event = typeof message.data === 'string' ? parseLiveEvent(message.data) : null;
          if (event) onEvent(event);
        };
      };

      open();
      watchdog = setInterval(() => {
        if (source !== null && now() - lastMessageAt > SILENCE_RECONNECT_MS) drop();
      }, HEARTBEAT_INTERVAL_MS);

      return () => {
        closed = true;
        if (retry !== null) clearTimeout(retry);
        if (watchdog !== null) clearInterval(watchdog);
        source?.close();
        source = null;
      };
    },
  };
}
